import React, { lazy, Suspense, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { onIdTokenChanged, sendEmailVerification, signOut, type User } from 'firebase/auth';
import { auth } from '../services/firebase';
import { hasVerifiedEmail, refreshVerifiedEmail, verificationActionSettings } from '../services/emailVerification';
import { LEGAL_IDENTITY } from '../legalIdentity';
import Login from './Login';

const UserProfileModal = lazy(() => import('./UserProfileModal').then((module) => ({ default: module.UserProfileModal })));
const Loading = () => <div className="min-h-[100dvh] bg-canvas text-ink grid place-items-center" role="status">Cargando Juris…</div>;

export default function EmailVerificationGate({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<{ user: User | null; verified: boolean } | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendAt, setResendAt] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [showAccount, setShowAccount] = useState(false);
  const refreshing = useRef(false);

  useEffect(() => {
    let generation = 0;
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      const current = ++generation;
      if (!user) {
        setSession({ user: null, verified: false });
        setShowAccount(false);
        return;
      }
      try {
        const token = await user.getIdTokenResult();
        if (current === generation) setSession({ user, verified: hasVerifiedEmail(token.claims) });
      } catch {
        if (current === generation) {
          setSession({ user, verified: false });
          setError('No se pudo comprobar tu sesión. Inténtalo de nuevo.');
        }
      }
    });
    return () => { generation++; unsubscribe(); };
  }, []);

  const user = session?.user;
  const verified = session?.verified;
  const check = useCallback(async (manual = false) => {
    if (!user || verified || refreshing.current) return;
    refreshing.current = true;
    try {
      const confirmed = await refreshVerifiedEmail(user);
      if (auth.currentUser !== user) return;
      if (confirmed) setSession({ user, verified: true });
      else if (manual) setMessage('Tu correo todavía no figura como verificado. Abre el enlace del mensaje y vuelve aquí.');
      if (manual) setError('');
    } catch {
      if (manual) setError('No se pudo comprobar la verificación. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      refreshing.current = false;
    }
  }, [user, verified]);

  useEffect(() => {
    if (!user || verified) return;
    const checkVisible = () => { if (document.visibilityState === 'visible') void check(); };
    checkVisible();
    const timer = window.setInterval(checkVisible, 10_000);
    window.addEventListener('focus', checkVisible);
    document.addEventListener('visibilitychange', checkVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', checkVisible);
      document.removeEventListener('visibilitychange', checkVisible);
    };
  }, [check, user, verified]);

  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, Math.ceil((resendAt - Date.now()) / 1000)));
    tick();
    if (!resendAt) return;
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [resendAt]);

  const send = async () => {
    if (!user || busy || Date.now() < resendAt) return;
    setBusy(true);
    setError('');
    try {
      auth.languageCode = 'es';
      await sendEmailVerification(user, verificationActionSettings);
      setMessage('Correo enviado. Revisa tu bandeja de entrada y la carpeta de spam.');
      setResendAt(Date.now() + 60_000);
    } catch (cause) {
      const code = (cause as { code?: string })?.code;
      setError(code === 'auth/too-many-requests'
        ? 'Se han enviado demasiadas solicitudes. Espera unos minutos antes de volver a intentarlo.'
        : 'No se pudo enviar el correo. Inténtalo de nuevo o solicita ayuda.');
    } finally { setBusy(false); }
  };

  if (!session) return <Loading />;
  if (!user) return <Login />;
  if (verified) return children;

  return (
    <main className="min-h-[100dvh] bg-canvas text-ink px-5 py-10 flex items-center justify-center">
      <section className="w-full max-w-md bg-panel border border-line rounded-2xl p-6 space-y-5">
        <img src="/juris-logo.png" alt="Juris" width={80} height={80} className="mx-auto" />
        <h1 className="text-2xl font-bold text-center">Verifica tu correo</h1>
        <p>Confirma <strong className="break-all">{user.email || 'tu dirección de correo'}</strong> para empezar a utilizar Juris.</p>
        <p className="text-sm">Abre el enlace del correo y vuelve aquí. El acceso se activará automáticamente. Tu cuenta, chats, suscripción y créditos se conservan.</p>
        {message && <p role="status" className="text-sm">{message}</p>}
        {error && <p role="alert" className="text-sm text-danger">{error}</p>}
        <button type="button" onClick={() => void send()} disabled={busy || remaining > 0 || !user.email} className="w-full rounded-xl bg-action text-white p-3 font-bold disabled:opacity-50">
          {busy ? 'Enviando…' : remaining > 0 ? `Reenviar en ${remaining} s` : resendAt ? 'Reenviar correo' : 'Enviar correo de verificación'}
        </button>
        <button type="button" onClick={() => void check(true)} className="w-full rounded-xl border border-line p-3">Ya he verificado mi correo</button>
        <div className="flex flex-wrap justify-center gap-4 text-sm underline">
          <button type="button" onClick={() => setShowAccount(true)}>Cuenta y suscripción</button>
          <a href={`mailto:${LEGAL_IDENTITY.email}`}>Solicitar ayuda</a>
          <button type="button" onClick={() => { void signOut(auth).catch(() => setError('No se pudo cerrar la sesión. Inténtalo de nuevo.')); }}>Cerrar sesión</button>
        </div>
        <p className="text-xs">En «Cuenta y suscripción» puedes gestionar tu suscripción o eliminar tu cuenta.</p>
      </section>
      {showAccount && <Suspense fallback={<Loading />}><UserProfileModal onClose={() => setShowAccount(false)} /></Suspense>}
    </main>
  );
}
